import type { Environment } from '@/types';
import { OS_TYPE } from '@/types/enums/common';

/**
 * 检测当前运行环境
 */
export const detectEnvironment = (): Environment => {
  const ua = navigator.userAgent.toLowerCase();
  const isWeChat = /micromessenger/i.test(ua);
  const isHarmony = /harmonyos|hongmeng|harmony/i.test(ua);

  // 检测是否在微信小程序中
  const isWeChatMini = ua.includes('micromessenger') && ua.includes('miniprogram');

  // 检测是否在App中（根据实际App注入的bridge判断，鸿蒙容器也视为 App）
  const isApp =
    !isWeChat &&
    (!!(window as any).webkit?.messageHandlers ||
      !!(window as any).appBridge ||
      !!(window as any).android ||
      isHarmony);

  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);

  // 检测屏幕方向
  const isLandscape = window.innerWidth > window.innerHeight;

  // 检测是否为开发环境
  const isDev = import.meta.env['VITE_NODE_ENV'] === 'development';
  const isTest = import.meta.env['VITE_NODE_ENV'] === 'test';
  const isProduction = import.meta.env['VITE_NODE_ENV'] === 'production';

  return {
    isDev,
    isTest,
    isProduction,
    isWeChat,
    isWeChatMini,
    isApp,
    isIOS,
    isAndroid,
    isLandscape,
  };
};

/**
 * 判断当前是否在 Belink 宿主 APP 的 WebView 中（通过 UA 标识）
 * 约定：APP 注入的 UA 包含 `BelinkHealth`（大小写不敏感）
 */
export const isInBelinkApp = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /belinkhealth/i.test(navigator.userAgent);
};

/**
 * 判断是否支持全屏API
 */
export const supportsFullscreen = (): boolean => {
  return !!(
    document.fullscreenEnabled ||
    (document as any).webkitFullscreenEnabled ||
    (document as any).mozFullScreenEnabled ||
    (document as any).msFullscreenEnabled
  );
};

/**
 * 请求全屏
 */
export const requestFullscreen = (element: HTMLElement): Promise<void> => {
  if (element.requestFullscreen) {
    return element.requestFullscreen();
  } else if ((element as any).webkitRequestFullscreen) {
    return (element as any).webkitRequestFullscreen();
  } else if ((element as any).mozRequestFullScreen) {
    return (element as any).mozRequestFullScreen();
  } else if ((element as any).msRequestFullscreen) {
    return (element as any).msRequestFullscreen();
  }
  return Promise.reject(new Error('Fullscreen not supported'));
};

/**
 * 退出全屏
 */
export const exitFullscreen = (): Promise<void> => {
  if (document.exitFullscreen) {
    return document.exitFullscreen();
  } else if ((document as any).webkitExitFullscreen) {
    return (document as any).webkitExitFullscreen();
  } else if ((document as any).mozCancelFullScreen) {
    return (document as any).mozCancelFullScreen();
  } else if ((document as any).msExitFullscreen) {
    return (document as any).msExitFullscreen();
  }
  return Promise.reject(new Error('Exit fullscreen not supported'));
};

/**
 * 判断当前运行的操作系统
 * @returns {OS_TYPE} 操作系统类型（IOS/ANDROID/HARMONY/PC）
 */
export const getOSType = (): OS_TYPE => {
  // 获取用户代理字符串并转为小写，方便匹配
  const userAgent = navigator.userAgent.toLowerCase();
  // 获取平台信息
  const platform = navigator.platform.toLowerCase();

  // ========== 判断鸿蒙系统（含纯血鸿蒙 OpenHarmony / ArkWeb 与华为壳鸿蒙） ==========
  // 与 detectEnvironment 的判定保持一致：/harmony/ 同时覆盖 harmonyos、openharmony、hongmeng
  if (/harmonyos|hongmeng|harmony/i.test(navigator.userAgent)) {
    return OS_TYPE.HARMONY;
  }

  // ========== 判断 iOS 系统 ==========
  const isIOS = /iphone|ipod|ipad/.test(platform);
  if (isIOS && !userAgent.includes('windows') && !userAgent.includes('android')) {
    return OS_TYPE.IOS;
  }

  // ========== 判断 Android 系统 ==========
  if (userAgent.includes('android') && !userAgent.includes('harmonyos')) {
    return OS_TYPE.ANDROID;
  }

  // ========== 其余情况判定为 PC 端 ==========
  return OS_TYPE.PC;
};
